/**
 * Phase 4 — Epic-Ready Export Panel
 * 
 * Unified export panel with 4 tabs:
 * 1. Epic Copy/Paste — Operative Note
 * 2. Epic Copy/Paste — CPT String
 * 3. PDF Summary (HIM / Audit)
 * 4. Structured JSON
 * 
 * Includes RBAC, audit logging, demo mode, and failure handling.
 */

import React, { useState, useMemo, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Copy,
  Download,
  FileText,
  FileJson,
  Sparkles,
  Package,
  Lock,
  Loader2,
  ShieldCheck,
  ClipboardCopy,
  Stethoscope,
  AlertTriangle,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useDemoMode } from '@/contexts/DemoModeContext';
import { toast } from 'sonner';
import { generateEpicOperativeNote, type OperativeNoteInput } from '@/lib/export/epicOperativeNoteGenerator';
import { generateEpicCptString, type CptStringInput } from '@/lib/export/epicCptStringGenerator';
import { generateEpicPdf, type EpicPdfInput, type ProofMapSummaryItem, type SurgeonConfirmationItem } from '@/lib/export/epicPdfGenerator';
import { generateEpicJson, type EpicJsonInput } from '@/lib/export/epicJsonGenerator';
import type { ExportCptLine, ExportAuditEvent } from '@/lib/export/exportPackageGenerator';
import type { FinalCptLine } from '@/hooks/useFinalCptSequencing';
import type { StructuredOpNote } from '@/lib/dictation/types';

// ============================================================================
// TYPES
// ============================================================================

type ExportRole = 'link_ops' | 'surgeon' | 'hospital_admin';
type ExportTab = 'op_note' | 'cpt_string' | 'pdf' | 'json';

interface EpicExportPanelProps {
  caseId: string | null;
  caseNumber?: string;
  authReference?: string;
  dateOfService?: string;
  laterality?: string;
  surgeonName?: string;
  assistantSurgeonName?: string;
  facilityName?: string;
  attestationText?: string;
  attestationTimestamp?: string;
  
  /** Operative note data */
  operativeNoteText?: string;
  structuredNote?: StructuredOpNote;
  
  /** Final CPT lines from Phase 3 */
  finalLines: FinalCptLine[];
  authorizedCpts: string[];
  cptDescriptions?: Record<string, string>;
  
  /** CPT states for confirmations */
  cptStates: Record<string, {
    surgeonConfirmation: string | null;
    billableStatus: string;
    documentationStatus?: string;
  }>;
  
  /** Proof map data */
  proofMapSummary?: ProofMapSummaryItem[];
  
  /** Audit timeline */
  auditTimeline?: ExportAuditEvent[];
  
  /** Gate status */
  canExport: boolean;
  gatesPassed: number;
  gatesTotal: number;
  
  /** Post-op state */
  postOpState?: string;
  
  /** User role */
  userRole?: ExportRole;
}

// ============================================================================
// COMPONENT
// ============================================================================

export function EpicExportPanel({
  caseId,
  caseNumber = 'CASE-UNKNOWN',
  authReference,
  dateOfService,
  laterality,
  surgeonName,
  assistantSurgeonName,
  facilityName,
  attestationText,
  attestationTimestamp,
  operativeNoteText,
  structuredNote,
  finalLines,
  authorizedCpts,
  cptDescriptions = {},
  cptStates,
  proofMapSummary,
  auditTimeline = [],
  canExport,
  gatesPassed,
  gatesTotal,
  postOpState,
  userRole = 'link_ops',
}: EpicExportPanelProps) {
  const { isDemoMode } = useDemoMode();
  const [activeTab, setActiveTab] = useState<ExportTab>('op_note');
  const [opNoteOutput, setOpNoteOutput] = useState<string | null>(null);
  const [cptStringOutput, setCptStringOutput] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);

  // Role checks
  const canUserExport = userRole === 'link_ops' || userRole === 'hospital_admin';
  const isSurgeonView = userRole === 'surgeon';
  const isPostOpValid = isDemoMode || postOpState === 'export_ready' || !postOpState;
  const exportAllowed = (canExport || isDemoMode) && isPostOpValid;

  // Build CPT lines for export
  const exportCptLines = useMemo<ExportCptLine[]>(() => {
    return finalLines.map(l => ({
      sequence: l.sequence,
      cptCode: l.cptCode,
      cptDescription: cptDescriptions[l.cptCode] || l.cptCode,
      modifiers: l.modifiers,
      units: l.units,
      finalCodeString: l.finalCodeString,
      modifierExplanations: l.modifierExplanations,
    }));
  }, [finalLines, cptDescriptions]);

  // Build surgeon confirmations
  const surgeonConfirmations = useMemo<SurgeonConfirmationItem[]>(() => {
    return authorizedCpts.map(cpt => ({
      cptCode: cpt,
      confirmation: cptStates[cpt]?.surgeonConfirmation || 'pending',
      billableStatus: cptStates[cpt]?.billableStatus || 'review_required',
    }));
  }, [authorizedCpts, cptStates]);

  // Audit logging
  const logExportEvent = useCallback(async (format: string, success: boolean) => {
    if (isDemoMode || !caseId) return;
    try {
      const { data: { user } } = await supabase.auth.getUser();
      await supabase.from('authorization_activity_log').insert({
        authorization_id: caseId,
        action: success ? 'export_generated' : 'export_failed',
        actor_id: user?.id || 'system',
        actor_type: userRole || 'link_ops',
        details: {
          format,
          caseNumber,
          cptCount: finalLines.length,
          gatesPassed,
          gatesTotal,
          timestamp: new Date().toISOString(),
        } as any,
      });
    } catch (err) {
      console.error('Failed to log export event:', err);
    }
  }, [isDemoMode, caseId, userRole, caseNumber, finalLines.length, gatesPassed, gatesTotal]);

  // ── GENERATE OPERATIVE NOTE ────────────────────────────────────────
  const handleGenerateOpNote = useCallback(async () => {
    if (!canUserExport && !isSurgeonView) {
      toast.error('Export not permitted');
      return;
    }

    setIsGenerating(true);
    try {
      const noteInput: OperativeNoteInput = {
        caseId: caseId || '',
        caseNumber,
        dateOfService,
        laterality,
        surgeonName: isDemoMode ? 'Dr. Michael Larkin, MD' : surgeonName,
        assistantSurgeonName,
        attestationTimestamp,
        preoperativeDiagnosis: structuredNote?.indications,
        postoperativeDiagnosis: structuredNote?.indications,
        proceduresPerformed: structuredNote?.procedures?.split('\n')[0],
        anesthesia: structuredNote?.anesthesia,
        indications: structuredNote?.indications,
        findings: structuredNote?.findings,
        procedureDescription: structuredNote?.procedures,
        implants: structuredNote?.implants,
        estimatedBloodLoss: structuredNote?.estimatedBloodLoss,
        complications: structuredNote?.complications,
        specimens: structuredNote?.specimens,
        closure: structuredNote?.closure,
        rawTranscript: operativeNoteText,
        isDemoExport: isDemoMode,
      };

      const output = generateEpicOperativeNote(noteInput);
      setOpNoteOutput(output);
      await logExportEvent('epic_operative_note', true);
      toast.success('Operative note generated for Epic');
    } catch (err) {
      console.error('Op note generation failed:', err);
      await logExportEvent('epic_operative_note', false);
      toast.error('Failed to generate operative note. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  }, [canUserExport, isSurgeonView, caseId, caseNumber, dateOfService, laterality, surgeonName, assistantSurgeonName, attestationTimestamp, structuredNote, operativeNoteText, isDemoMode, logExportEvent]);

  // ── GENERATE CPT STRING ────────────────────────────────────────────
  const handleGenerateCptString = useCallback(async () => {
    if (!canUserExport) {
      toast.error('Export not permitted', { description: 'Contact LINK Ops for CPT export access.' });
      return;
    }

    setIsGenerating(true);
    try {
      const cptInput: CptStringInput = {
        caseNumber,
        authReference,
        dateOfService,
        laterality,
        surgeonName: isDemoMode ? 'Dr. Michael Larkin, MD' : surgeonName,
        cptLines: exportCptLines,
        isDemoExport: isDemoMode,
        includeReferenceTags: true,
      };

      const output = generateEpicCptString(cptInput);
      setCptStringOutput(output);
      await logExportEvent('epic_cpt_string', true);
      toast.success('CPT string block generated');
    } catch (err) {
      console.error('CPT string generation failed:', err);
      await logExportEvent('epic_cpt_string', false);
      toast.error('Failed to generate CPT string. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  }, [canUserExport, caseNumber, authReference, dateOfService, laterality, surgeonName, exportCptLines, isDemoMode, logExportEvent]);

  // ── GENERATE PDF ───────────────────────────────────────────────────
  const handleGeneratePdf = useCallback(async () => {
    if (!canUserExport) {
      toast.error('Export not permitted');
      return;
    }

    setIsGenerating(true);
    try {
      const pdfInput: EpicPdfInput = {
        caseNumber,
        authReference,
        dateOfService,
        laterality,
        surgeonName: isDemoMode ? 'Dr. Michael Larkin, MD' : surgeonName,
        assistantSurgeonName,
        facilityName: isDemoMode ? 'Cincinnati Orthopedic Institute' : facilityName,
        attestationText: isDemoMode
          ? 'I attest this operative note accurately reflects the care provided.'
          : attestationText,
        attestationTimestamp: isDemoMode ? new Date().toISOString() : attestationTimestamp,
        operativeNoteText,
        cptLines: exportCptLines,
        authorizedCpts,
        authorizationScope: 'Standard',
        proofMapSummary,
        surgeonConfirmations,
        auditTimeline,
        isDemoExport: isDemoMode,
        generatedAt: new Date().toISOString(),
      };

      const doc = generateEpicPdf(pdfInput);
      const filename = isDemoMode
        ? `LINK_DEMO_${caseNumber}_export.pdf`
        : `LINK_${caseNumber}_export.pdf`;
      doc.save(filename);
      await logExportEvent('pdf_summary', true);
      toast.success('PDF downloaded', { description: filename });
    } catch (err) {
      console.error('PDF generation failed:', err);
      await logExportEvent('pdf_summary', false);
      toast.error('Failed to generate PDF. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  }, [canUserExport, caseNumber, authReference, dateOfService, laterality, surgeonName, assistantSurgeonName, facilityName, attestationText, attestationTimestamp, operativeNoteText, exportCptLines, authorizedCpts, proofMapSummary, surgeonConfirmations, auditTimeline, isDemoMode, logExportEvent]);

  // ── GENERATE JSON ──────────────────────────────────────────────────
  const handleGenerateJson = useCallback(async () => {
    if (!canUserExport) {
      toast.error('Export not permitted');
      return;
    }

    setIsGenerating(true);
    try {
      const jsonInput: EpicJsonInput = {
        caseId: caseId || '',
        caseNumber,
        dateOfService,
        laterality,
        facilityName: isDemoMode ? 'Cincinnati Orthopedic Institute' : facilityName,
        authReference,
        authorizationScope: 'Standard',
        authorizedCpts,
        surgeonName: isDemoMode ? 'Dr. Michael Larkin, MD' : surgeonName,
        assistantSurgeonName,
        attestationText: isDemoMode
          ? 'I attest this operative note accurately reflects the care provided.'
          : attestationText,
        attestationTimestamp: isDemoMode ? new Date().toISOString() : attestationTimestamp,
        operativeNoteText,
        cptLines: exportCptLines,
        evidenceReferences: proofMapSummary?.map(pm => ({
          cptCode: pm.cptCode,
          evidenceSnippetIds: [],
          documentationStatus: pm.documentationStatus,
          requirementsSatisfied: pm.requirementsSatisfied,
          requirementsTotal: pm.requirementsTotal,
        })),
        confirmations: surgeonConfirmations,
        auditTimeline,
        gatesPassed,
        gatesTotal,
        isDemoExport: isDemoMode,
      };

      const payload = generateEpicJson(jsonInput);
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = isDemoMode
        ? `LINK_DEMO_${caseNumber}_export.json`
        : `LINK_${caseNumber}_export.json`;
      a.click();
      URL.revokeObjectURL(url);
      await logExportEvent('json', true);
      toast.success('JSON export downloaded');
    } catch (err) {
      console.error('JSON generation failed:', err);
      await logExportEvent('json', false);
      toast.error('Failed to generate JSON export. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  }, [canUserExport, caseId, caseNumber, dateOfService, laterality, facilityName, authReference, authorizedCpts, surgeonName, assistantSurgeonName, attestationText, attestationTimestamp, operativeNoteText, exportCptLines, proofMapSummary, surgeonConfirmations, auditTimeline, gatesPassed, gatesTotal, isDemoMode, logExportEvent]);

  // ── COPY TO CLIPBOARD ──────────────────────────────────────────────
  const handleCopy = useCallback((text: string, label: string) => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success(`${label} copied to clipboard`);
    }).catch(() => {
      toast.error('Failed to copy to clipboard');
    });
  }, []);

  return (
    <TooltipProvider>
      <Card className={cn(
        'border-2',
        exportAllowed ? 'border-success/50' : 'border-destructive/30',
      )}>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-lg">
                {exportAllowed ? (
                  <Package className="h-5 w-5 text-success" />
                ) : (
                  <Lock className="h-5 w-5 text-destructive" />
                )}
                Epic-Ready Export
                {isDemoMode && (
                  <Badge variant="outline" className="ml-2 text-xs bg-warning/10 text-warning border-warning/30">
                    Demo — Illustrative Only
                  </Badge>
                )}
              </CardTitle>
              <CardDescription>
                {exportAllowed
                  ? 'Generate audit-ready handoff packages for hospital billing and HIM'
                  : 'Resolve blocking issues to enable export'}
              </CardDescription>
            </div>
            <Badge
              variant={exportAllowed ? 'default' : 'destructive'}
              className={cn('text-sm px-3 py-1', exportAllowed && 'bg-success hover:bg-success/90')}
            >
              {gatesPassed}/{gatesTotal} gates
            </Badge>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Demo banner */}
          {isDemoMode && (
            <Alert className="border-warning/50 bg-warning/10">
              <Sparkles className="h-4 w-4 text-warning" />
              <AlertTitle className="text-warning">Demo Mode — Illustrative Only — No Claims Submitted</AlertTitle>
              <AlertDescription className="text-xs text-muted-foreground">
                This export uses simulated data. No PHI is stored or transmitted.
              </AlertDescription>
            </Alert>
          )}

          {/* Post-op state gate */}
          {!isPostOpValid && !isDemoMode && (
            <Alert variant="destructive">
              <Lock className="h-4 w-4" />
              <AlertTitle>Case Not Export Ready</AlertTitle>
              <AlertDescription>
                Current state: <strong>{postOpState || 'unknown'}</strong> — complete reconciliation first.
              </AlertDescription>
            </Alert>
          )}

          {/* Surgeon view-only notice */}
          {isSurgeonView && (
            <Alert className="border-primary/30 bg-primary/5">
              <ShieldCheck className="h-4 w-4 text-primary" />
              <AlertTitle>Surgeon View</AlertTitle>
              <AlertDescription className="text-xs">
                You can view and copy the operative note. CPT export and PDF download require LINK Ops or Billing access.
              </AlertDescription>
            </Alert>
          )}

          {/* Export tabs */}
          {exportAllowed && (
            <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as ExportTab)}>
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="op_note" className="gap-1 text-xs">
                  <Stethoscope className="h-3.5 w-3.5" />
                  Op Note
                </TabsTrigger>
                <TabsTrigger value="cpt_string" className="gap-1 text-xs" disabled={isSurgeonView}>
                  <ClipboardCopy className="h-3.5 w-3.5" />
                  CPT Codes
                </TabsTrigger>
                <TabsTrigger value="pdf" className="gap-1 text-xs" disabled={isSurgeonView}>
                  <FileText className="h-3.5 w-3.5" />
                  PDF
                </TabsTrigger>
                <TabsTrigger value="json" className="gap-1 text-xs" disabled={isSurgeonView}>
                  <FileJson className="h-3.5 w-3.5" />
                  JSON
                </TabsTrigger>
              </TabsList>

              {/* Tab 1: Operative Note */}
              <TabsContent value="op_note" className="mt-4 space-y-3">
                <p className="text-sm text-muted-foreground">
                  Generate a clean, Epic-standard operative note formatted for copy/paste.
                  Content is derived exclusively from surgeon dictation.
                </p>
                {opNoteOutput ? (
                  <div className="space-y-2">
                    <Textarea
                      value={opNoteOutput}
                      readOnly
                      className="font-mono text-xs min-h-[300px] bg-muted/30"
                    />
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleCopy(opNoteOutput, 'Operative note')}
                        className="gap-1"
                      >
                        <Copy className="h-3 w-3" />
                        Copy to Clipboard
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setOpNoteOutput(null)}
                      >
                        Regenerate
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Button
                    onClick={handleGenerateOpNote}
                    disabled={isGenerating}
                    className="gap-2"
                  >
                    {isGenerating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Stethoscope className="h-4 w-4" />}
                    Generate Operative Note
                  </Button>
                )}
              </TabsContent>

              {/* Tab 2: CPT String */}
              <TabsContent value="cpt_string" className="mt-4 space-y-3">
                <p className="text-sm text-muted-foreground">
                  Generate a billing code block with ordered CPT lines, modifiers, units, 
                  and authorization references for hospital billing.
                </p>
                {cptStringOutput ? (
                  <div className="space-y-2">
                    <Textarea
                      value={cptStringOutput}
                      readOnly
                      className="font-mono text-xs min-h-[200px] bg-muted/30"
                    />
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleCopy(cptStringOutput, 'CPT codes')}
                        className="gap-1"
                      >
                        <Copy className="h-3 w-3" />
                        Copy CPTs
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setCptStringOutput(null)}
                      >
                        Regenerate
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Button
                    onClick={handleGenerateCptString}
                    disabled={isGenerating || isSurgeonView}
                    className="gap-2"
                  >
                    {isGenerating ? <Loader2 className="h-4 w-4 animate-spin" /> : <ClipboardCopy className="h-4 w-4" />}
                    Generate CPT String
                  </Button>
                )}
              </TabsContent>

              {/* Tab 3: PDF Summary */}
              <TabsContent value="pdf" className="mt-4 space-y-3">
                <p className="text-sm text-muted-foreground">
                  Download a comprehensive PDF with operative note, reconciliation table, proof map,
                  surgeon confirmations, authorization scope, and full audit timeline.
                </p>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="p-2 rounded bg-muted/30 flex items-center gap-2">
                    <FileText className="h-3.5 w-3.5 text-muted-foreground" />
                    <span>{exportCptLines.length} CPT lines</span>
                  </div>
                  <div className="p-2 rounded bg-muted/30 flex items-center gap-2">
                    <Stethoscope className="h-3.5 w-3.5 text-muted-foreground" />
                    <span>{surgeonConfirmations.filter(c => c.confirmation === 'confirmed_performed').length} confirmed</span>
                  </div>
                </div>
                <Button
                  onClick={handleGeneratePdf}
                  disabled={isGenerating || isSurgeonView}
                  className="gap-2"
                >
                  {isGenerating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                  Download PDF Summary
                </Button>
              </TabsContent>

              {/* Tab 4: JSON Export */}
              <TabsContent value="json" className="mt-4 space-y-3">
                <p className="text-sm text-muted-foreground">
                  Download a structured JSON payload for future system integration.
                  Includes all case metadata, CPT lines, evidence references, and attestations.
                </p>
                <Button
                  onClick={handleGenerateJson}
                  disabled={isGenerating || isSurgeonView}
                  className="gap-2"
                >
                  {isGenerating ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileJson className="h-4 w-4" />}
                  Download JSON
                </Button>
              </TabsContent>
            </Tabs>
          )}

          {/* Footer disclaimer */}
          <div className="flex items-start gap-2 p-3 bg-muted/30 rounded-lg border border-border text-xs text-muted-foreground">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
            <p>
              <span className="font-medium">LINK does not submit claims.</span>{' '}
              These exports are designed for hospital billing and HIM teams to paste or upload
              into Epic, NextGen, or similar EHR systems for final review and claim submission.
            </p>
          </div>
        </CardContent>
      </Card>
    </TooltipProvider>
  );
}
